import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUsers, FiHardDrive, FiUpload, FiActivity, FiServer, FiDatabase } = FiIcons;

function SystemStats() {
  const stats = [
    {
      label: 'Total Users',
      value: '24',
      change: '+3 this month',
      icon: FiUsers,
      color: 'blue'
    },
    {
      label: 'Storage Used',
      value: '2.4 TB',
      change: '+180 GB this month',
      icon: FiHardDrive,
      color: 'green'
    },
    {
      label: 'Files Uploaded',
      value: '1,234',
      change: '+89 this week',
      icon: FiUpload,
      color: 'purple'
    },
    {
      label: 'Active Sessions',
      value: '12',
      change: 'Currently online',
      icon: FiActivity,
      color: 'orange'
    }
  ];

  const systemHealth = [
    { service: 'S3 Storage', status: 'healthy', uptime: '99.9%' },
    { service: 'Authentication', status: 'healthy', uptime: '99.8%' },
    { service: 'File Processing', status: 'warning', uptime: '98.2%' },
    { service: 'Database', status: 'healthy', uptime: '99.9%' }
  ];

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">System Statistics</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-lg p-6 border border-gray-200"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 bg-${stat.color}-100 rounded-lg flex items-center justify-center`}>
                <SafeIcon icon={stat.icon} className={`w-6 h-6 text-${stat.color}-600`} />
              </div>
            </div>
            
            <h3 className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</h3>
            <p className="text-sm text-gray-600 mb-2">{stat.label}</p>
            <p className="text-xs text-green-600 font-medium">{stat.change}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-lg p-6 border border-gray-200"
        >
          <h3 className="text-lg font-medium text-gray-900 mb-4">System Health</h3>
          
          <div className="space-y-4">
            {systemHealth.map((service, index) => (
              <div key={service.service} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${
                    service.status === 'healthy' ? 'bg-green-500' :
                    service.status === 'warning' ? 'bg-yellow-500' : 'bg-red-500'
                  }`} />
                  <span className="text-sm font-medium text-gray-900">{service.service}</span>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">{service.uptime}</p>
                  <p className={`text-xs capitalize ${
                    service.status === 'healthy' ? 'text-green-600' :
                    service.status === 'warning' ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {service.status}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-lg p-6 border border-gray-200"
        >
          <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h3>
          
          <div className="space-y-4">
            {[
              { action: 'New user registered', user: 'john.doe@example.com', time: '2 min ago' },
              { action: 'File uploaded', user: 'jane.smith@example.com', time: '5 min ago' },
              { action: 'Storage quota updated', user: 'admin', time: '1 hour ago' },
              { action: 'System backup completed', user: 'system', time: '2 hours ago' }
            ].map((activity, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2" />
                <div className="flex-1">
                  <p className="text-sm text-gray-900">{activity.action}</p>
                  <p className="text-xs text-gray-500">{activity.user} • {activity.time}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default SystemStats;